import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  FileText,
  Bell,
  Send,
  Settings,
  ArrowLeft,
  Shield,
  Menu,
  X,
  ClipboardList,
} from 'lucide-react';

const navItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { path: '/admin/users', icon: Users, label: 'Usuarios' },
  { path: '/admin/records', icon: FileText, label: 'Registros' },
  { path: '/admin/audit', icon: ClipboardList, label: 'Audit Log' },
  { path: '/admin/reminders', icon: Bell, label: 'Recordatorios' },
  { path: '/admin/notifications', icon: Send, label: 'Notificaciones' },
  { path: '/admin/system', icon: Settings, label: 'Sistema' },
];

export const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const closeSidebar = () => setSidebarOpen(false);

  // Cerrar sidebar al navegar en movil
  const handleNavClick = () => {
    setSidebarOpen(false);
  };

  // Titulo de la pagina actual
  const currentPage = navItems.find(item =>
    item.end ? location.pathname === item.path : location.pathname.startsWith(item.path)
  );

  return (
    <div className="min-h-screen bg-[var(--color-bg)]">
      {/* Mobile Header */}
      {/* paddingTop combina el padding visual con el safe-area de iOS para que
          el boton de menu no quede debajo del reloj/notch de Apple */}
      <div
        className="lg:hidden sticky top-0 z-30 bg-[var(--color-surface)] border-b border-[var(--color-border)] px-4 pb-3 flex items-center gap-3"
        style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
      >
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-2 rounded-xl hover:bg-[var(--color-bg)] transition-colors"
        >
          <Menu size={22} className="text-[var(--color-text)]" />
        </button>
        <div className="flex items-center gap-2">
          <Shield size={18} style={{ color: '#6366F1' }} />
          <span className="font-semibold text-[var(--color-text)]">
            {currentPage?.label || 'Admin'}
          </span>
        </div>
      </div>

      <div className="flex">
        {/* Mobile Overlay */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={closeSidebar}
            />
          )}
        </AnimatePresence>

        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky top-0 left-0 h-full lg:h-screen w-64 bg-[var(--color-surface)] border-r border-[var(--color-border)] flex flex-col z-50
            transition-transform duration-300 ease-in-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          {/* Header */}
          <div
            className="px-4 pb-4 border-b border-[var(--color-border)] flex items-center justify-between"
            style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))' }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)' }}
              >
                <Shield size={20} className="text-white" />
              </div>
              <div>
                <h1 className="font-bold text-[var(--color-text)]">Admin Panel</h1>
                <p className="text-xs text-[var(--color-text-muted)]">MalDolor v2</p>
              </div>
            </div>
            <button
              onClick={closeSidebar}
              className="lg:hidden p-2 rounded-xl hover:bg-[var(--color-bg)] transition-colors"
            >
              <X size={20} className="text-[var(--color-text-muted)]" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                onClick={handleNavClick}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    isActive
                      ? 'bg-[var(--color-accent)] text-white'
                      : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]'
                  }`
                }
              >
                <item.icon size={20} />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Back to App */}
          <div
            className="p-3 border-t border-[var(--color-border)]"
            style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
          >
            <button
              onClick={() => { navigate('/'); closeSidebar(); }}
              className="flex items-center gap-3 px-4 py-3 rounded-xl w-full text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)] transition-all"
            >
              <ArrowLeft size={20} />
              <span className="font-medium">Volver a la App</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 overflow-auto">
          <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
